import { StaticContentItem } from "../types/Model";
import {
  ACTION_TYPES,
  createData,
  getDataWithParams,
  updateData,
} from "./modelUtils";

const STATIC_CONTENT_IDS = {
  announcement: "announcement",
  welcome: "welcome",
  helpAndResources: "helpAndResources",
  servicesAndFeatures: "servicesAndFeatures",
  languageOptions: "languageOptions",
  verticalOptions: "verticalOptions",
} as const;

type StaticContentKey = keyof typeof STATIC_CONTENT_IDS;

// Base functions for static content operations
export async function getStaticContent(id: string, labelLogName: string) {
  return await getDataWithParams({
    action: ACTION_TYPES.GET_STATIC_CONTENT,
    labelLogName,
    dataField: "staticContent",
    params: {
      id,
      idFieldName: "staticContentId",
    },
  });
}

export async function createStaticContent(
  id: string,
  labelLogName: string,
  { lexicalNode, htmlText }: StaticContentItem
) {
  return await createData({
    action: ACTION_TYPES.CREATE_STATIC_CONTENT,
    labelLogName,
    dataField: "staticContent",
    fields: {
      lexicalNode,
      htmlText,
      staticContentId: id,
    },
  });
}

export async function updateStaticContent(
  staticContentId: string,
  labelLogName: string,
  { lexicalNode, htmlText }: StaticContentItem
) {
  return await updateData({
    action: ACTION_TYPES.UPDATE_STATIC_CONTENT,
    labelLogName,
    fields: { lexicalNode, htmlText },
    id: staticContentId,
    idFieldName: "staticContentId",
  });
}

// Generic function to get static content options
export async function getStaticContentOptions(
  staticContentId: string
): Promise<string[]> {
  return await getDataWithParams({
    action: ACTION_TYPES.GET_STATIC_CONTENT_OPTIONS,
    labelLogName: `${staticContentId} options`,
    dataField: "options",
    params: {
      id: staticContentId,
      idFieldName: "staticContentId",
    },
  });
}

// Factory function to create content handlers for each type
function createContentHandlers(key: StaticContentKey) {
  const id = STATIC_CONTENT_IDS[key];
  return {
    get: async () => getStaticContent(id, id),
    getOptions: async () => getStaticContentOptions(id),
    create: async ({ lexicalNode, htmlText }: StaticContentItem) =>
      createStaticContent(id, id, { lexicalNode, htmlText }),
    update: async ({ lexicalNode, htmlText }: StaticContentItem) =>
      updateStaticContent(id, id, { lexicalNode, htmlText }),
  };
}

// Create handlers for each content type
export const announcementModel = createContentHandlers("announcement");
export const welcomeModel = createContentHandlers("welcome");
export const helpAndResourcesModel = createContentHandlers("helpAndResources");
export const servicesAndFeaturesModel = createContentHandlers(
  "servicesAndFeatures"
);
export const languageOptionsModel = createContentHandlers("languageOptions");
export const verticalOptionsModel = createContentHandlers("verticalOptions");
